import { AlertTriangle } from "lucide-react";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { cn } from "@/utils/cn";

interface StatusConfig {
  label: string;
  tone: BadgeTone;
  warn?: boolean;
}

/**
 * Maps every real backend status string (packages/common/src/constants/domain.ts
 * enums: RideStatus, DriverStatus, DriverAvailability, KycStatus, AccountStatus,
 * SettlementStatus) to the wireframe's pill styling in one place.
 */
const STATUS_MAP: Record<string, StatusConfig> = {
  // RideStatus
  REQUESTED: { label: "Requested", tone: "neutral" },
  SEARCHING: { label: "Searching", tone: "neutral" },
  DRIVER_ASSIGNED: { label: "To pickup", tone: "neutral" },
  DRIVER_ARRIVING: { label: "To pickup", tone: "neutral" },
  DRIVER_ARRIVED: { label: "At pickup", tone: "neutral" },
  TRIP_STARTED: { label: "On trip", tone: "teal" },
  TRIP_COMPLETED: { label: "Completed", tone: "teal" },
  PAYMENT_PENDING: { label: "Collecting", tone: "gold" },
  PAYMENT_COMPLETED: { label: "Paid", tone: "teal" },
  RATED: { label: "Rated", tone: "teal" },
  CANCELLED: { label: "Cancelled", tone: "neutral" },

  // DriverStatus
  PENDING_KYC: { label: "Pending KYC", tone: "gold" },
  APPROVED: { label: "Approved", tone: "teal" },
  REJECTED: { label: "Rejected", tone: "neutral" },
  SUSPENDED: { label: "Suspended", tone: "danger" },
  ACTIVE: { label: "Active", tone: "teal" },
  INACTIVE: { label: "Inactive", tone: "neutral" },

  // DriverAvailability
  OFFLINE: { label: "Offline", tone: "neutral" },
  ONLINE: { label: "Online", tone: "teal" },
  ON_TRIP: { label: "On trip", tone: "teal" },

  // KycStatus
  PENDING: { label: "Pending", tone: "gold" },
  IN_REVIEW: { label: "In review", tone: "gold" },
  VERIFIED: { label: "Verified", tone: "teal" },
  EXPIRED: { label: "Expired", tone: "danger", warn: true },

  // AccountStatus
  PAUSED: { label: "Paused", tone: "neutral" },
  FLAGGED: { label: "Flagged", tone: "danger", warn: true },
  BLOCKED: { label: "Blocked", tone: "danger" },

  // SettlementStatus
  PROCESSING: { label: "Processing", tone: "gold" },
  PAID: { label: "Paid", tone: "teal" },
  FAILED: { label: "Failed", tone: "danger", warn: true },
};

export function StatusBadge({
  status,
  label,
  alert,
  className,
}: {
  status: string;
  /** Override the displayed text (e.g. the backend's precomputed statusLabel). */
  label?: string;
  /** Force the red/alert styling regardless of the mapped tone (e.g. a delayed ride). */
  alert?: boolean;
  className?: string;
}) {
  const config = STATUS_MAP[status] ?? { label: status, tone: "neutral" as BadgeTone };
  const tone = alert ? "danger" : config.tone;
  const warn = alert || config.warn;
  return (
    <Badge tone={tone} className={cn(className)}>
      {warn && <AlertTriangle className="h-3 w-3" />}
      {label ?? config.label}
    </Badge>
  );
}
